import { Fragment, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Dialog, DialogPanel, DialogTitle, Transition, TransitionChild } from "@headlessui/react";
import cn from "classnames";
import XIcon from "@icons/x-close.svg?react";
import type { EditChoice, EditRow, EditRowsDocument } from "@/common/lib/api/documentEdits";
import { EDIT_PRIMARY_BUTTON, EDIT_SECONDARY_BUTTON } from "./editButtonClasses";
import { differsOnlyInWhitespace } from "./editDiff";
import { EditRowDiff, rowSectionLabel } from "./EditRowDiff";
import { KEEP_EDITS_COPY as C, TAG_LABELS, TRIGGER_COPY, type KeepEditsTrigger } from "./keepEditsCopy";

export type { KeepEditsTrigger };

type Props = {
    isOpen: boolean;
    trigger: KeepEditsTrigger;
    documents: EditRowsDocument[];
    /** The section being regenerated, for the "regenerateSection" trigger. */
    sectionName?: string;
    /** Overrides the trigger's confirm button (sidebar Generate uses the Retry copy with a "Generate" button). */
    confirmLabel?: string;
    /** False when cancelling cannot stop the generation (Stop & Generate, D22): Close and Escape still call onCancel. */
    showCancel?: boolean;
    onCancel: () => void;
    onConfirm: (choices: EditChoice[]) => void;
};

const TAG_CLASS: Record<NonNullable<EditRow["tag"]>, string> = {
    renamed: "bg-gray-100 text-gray-700",
    reverted_to_normal: "bg-max-25 text-max-900 border border-max-100",
    added: "bg-[#ecfdf3] text-green-700",
    cleared: "bg-blaze-25 text-blaze-800",
};

/** Below this much room for the row list, the whole body scrolls instead of the list (D17 still holds when there is room). */
const MIN_LIST_HEIGHT = 240;

/** Discard state is keyed by document index and row id, since row ids repeat across documents. */
const keyOf = (docIndex: number, rowId: string) => `${docIndex}:${rowId}`;

const SegmentedToggle = ({
    label,
    keepLabel,
    discardLabel,
    keep,
    discard,
    onKeep,
    onDiscard,
}: {
    label: string;
    keepLabel: string;
    discardLabel: string;
    keep: boolean;
    discard: boolean;
    onKeep: () => void;
    onDiscard: () => void;
}) => {
    const name = useId();
    const seg = (on: boolean) =>
        cn(
            "relative flex min-h-11 items-center justify-center text-sm leading-5 cursor-pointer focus-within:ring-2 focus-within:ring-inset focus-within:ring-max-900",
            on ? "bg-max-900 font-semibold text-white" : "bg-white text-gray-700",
        );
    return (
        <div
            role="radiogroup"
            aria-label={label}
            className="grid w-full flex-none grid-cols-2 overflow-hidden rounded-md border border-gray-300 sm:w-[200px] lg:w-[240px]"
        >
            <label className={seg(keep)}>
                <input
                    type="radio"
                    name={name}
                    checked={keep}
                    onChange={onKeep}
                    aria-label={`${keepLabel} ${label}`}
                    className="absolute h-px w-px opacity-0"
                />
                {keepLabel}
            </label>
            <label className={seg(discard)}>
                <input
                    type="radio"
                    name={name}
                    checked={discard}
                    onChange={onDiscard}
                    aria-label={`${discardLabel} ${label}`}
                    className="absolute h-px w-px opacity-0"
                />
                {discardLabel}
            </label>
        </div>
    );
};

const BigCard = ({
    name,
    title,
    description,
    checked,
    onSelect,
}: {
    name: string;
    title: string;
    description: string;
    checked: boolean;
    onSelect: () => void;
}) => (
    <label
        className={cn(
            "flex min-h-[104px] cursor-pointer flex-col gap-1.5 rounded-xl p-4 hover:border-max-700 focus-within:outline focus-within:outline-2 focus-within:outline-offset-2 focus-within:outline-max-900",
            checked ? "border-2 border-max-900 bg-max-25" : "m-px border border-gray-300 bg-white",
        )}
    >
        <span className="flex items-center gap-2.5">
            <input
                type="radio"
                name={name}
                checked={checked}
                onChange={onSelect}
                aria-label={title}
                className="m-0 h-[18px] w-[18px] flex-none accent-max-900"
            />
            <span className="text-base font-semibold leading-6">{title}</span>
        </span>
        <span className="pl-7 text-sm leading-5 text-gray-600">{description}</span>
    </label>
);

const KeepEditsModal = ({
    isOpen,
    trigger,
    documents,
    sectionName,
    confirmLabel,
    showCancel = true,
    onCancel,
    onConfirm,
}: Props) => {
    const [discarded, setDiscarded] = useState<Record<string, boolean>>({});
    const [open, setOpen] = useState(false);
    // D6: every opening starts at Keep all with the list collapsed. Choices are keyed by document index, so they
    // also reset when the documents change.
    useEffect(() => {
        if (!isOpen) return;
        setDiscarded({});
        setOpen(false);
    }, [isOpen, documents]);
    const cardsName = useId();
    // When the list would get less than MIN_LIST_HEIGHT, the body scrolls as one and the list flows inside it.
    const bodyRef = useRef<HTMLDivElement>(null);
    const cardsRef = useRef<HTMLDivElement>(null);
    const toggleRef = useRef<HTMLButtonElement>(null);
    const [flow, setFlow] = useState(false);
    const copy = TRIGGER_COPY[trigger];

    useLayoutEffect(() => {
        const body = bodyRef.current;
        const cards = cardsRef.current;
        const toggle = toggleRef.current;
        if (!isOpen || !body || !cards || !toggle) return;
        const measure = () => {
            // Nothing is laid out (tests, hidden): leave the list its own scroll.
            if (!body.clientHeight) return;
            const style = getComputedStyle(body);
            const inner = body.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
            // the list wrapper's top/bottom border adds 2px, the flex gap between cards and list 16px
            setFlow(inner - cards.offsetHeight - toggle.offsetHeight - 18 < MIN_LIST_HEIGHT);
        };
        measure();
        if (typeof ResizeObserver === "undefined") return;
        const observer = new ResizeObserver(measure);
        observer.observe(body);
        observer.observe(cards);
        return () => observer.disconnect();
    }, [isOpen, open, documents]);

    const allKeys = useMemo(() => documents.flatMap((d, di) => d.rows.map((r) => keyOf(di, r.id))), [documents]);
    const total = allKeys.length;
    const kept = allKeys.filter((k) => !discarded[k]).length;
    const keepAll = kept === total;
    const discardAll = kept === 0;
    const several = documents.length > 1;

    const setMany = (keys: string[], discard: boolean) =>
        setDiscarded((prev) => {
            const next = { ...prev };
            keys.forEach((k) => {
                if (discard) next[k] = true;
                else delete next[k];
            });
            return next;
        });

    const title = several
        ? C.titleSeveral(documents.length)
        : trigger === "regenerateSection" && sectionName
          ? C.titleSection(sectionName, documents[0]?.header ?? "")
          : C.titleOne(documents[0]?.header ?? "");

    const confirm = () =>
        onConfirm(
            documents.map((d, di) => ({
                documentTypeId: d.documentTypeId,
                patientName: d.patientName,
                sourceChatCompletionId: d.sourceChatCompletionId,
                keepRowIds: d.rows.filter((r) => !discarded[keyOf(di, r.id)]).map((r) => r.id),
            })),
        );

    const pillClass =
        keepAll || discardAll
            ? "bg-gray-100 text-gray-600"
            : "bg-tropicana-25 text-tropicana-900 border border-tropicana-200";

    return (
        <Transition show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onCancel} static>
                <TransitionChild
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
                </TransitionChild>
                <div className="fixed inset-0 z-10 flex items-center justify-center px-4">
                    <TransitionChild
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 scale-95"
                        enterTo="opacity-100 scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 scale-100"
                        leaveTo="opacity-0 scale-95"
                    >
                        {/* D17: capped at the window minus 32px top and bottom; only the row list scrolls when there is room (B4). */}
                        <DialogPanel className="flex max-h-[calc(100dvh-64px)] w-full max-w-[720px] flex-col gap-5 rounded-lg bg-white pb-6 pt-5 text-left shadow-xl lg:max-w-[760px]">
                            <div className="flex-none border-b border-gray-200 px-6 pb-4">
                                <div className="flex items-center justify-between">
                                    <DialogTitle as="h3" className="m-0 text-lg font-semibold leading-6 text-gray-900">
                                        {title}
                                    </DialogTitle>
                                    <button
                                        type="button"
                                        aria-label="Close dialog"
                                        className="-m-2.5 flex h-11 w-11 flex-none items-center justify-center rounded-md text-gray-400 hover:text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-max-900"
                                        onClick={onCancel}
                                    >
                                        <XIcon className="h-6 w-6" />
                                    </button>
                                </div>
                                <div className="mt-2 text-sm leading-5 text-gray-700">
                                    {copy.subheader.replace("<section>", () => sectionName ?? "")}
                                </div>
                            </div>

                            <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-1">
                                <div
                                    ref={cardsRef}
                                    role="radiogroup"
                                    aria-label={C.cardsLabel}
                                    className="grid flex-none grid-cols-1 gap-3 sm:grid-cols-2"
                                >
                                    <BigCard
                                        name={cardsName}
                                        title={C.keepAllTitle}
                                        description={copy.keep}
                                        checked={keepAll}
                                        onSelect={() => setMany(allKeys, false)}
                                    />
                                    <BigCard
                                        name={cardsName}
                                        title={C.discardAllTitle}
                                        description={copy.discard}
                                        checked={discardAll}
                                        onSelect={() => setMany(allKeys, true)}
                                    />
                                </div>

                                <div
                                    className={cn(
                                        "flex flex-col overflow-hidden rounded-lg border border-gray-200",
                                        flow ? "flex-none" : "min-h-[50px]",
                                    )}
                                >
                                    <button
                                        ref={toggleRef}
                                        type="button"
                                        aria-expanded={open}
                                        onClick={() => setOpen((o) => !o)}
                                        className="flex min-h-12 w-full flex-none items-center gap-2.5 bg-white px-3.5 text-left text-sm font-semibold leading-5 text-gray-900 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-max-900"
                                    >
                                        <svg
                                            width="16"
                                            height="16"
                                            viewBox="0 0 24 24"
                                            fill="none"
                                            stroke="currentColor"
                                            strokeWidth="2"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            aria-hidden="true"
                                            className={cn(
                                                "flex-none text-gray-600 transition-transform",
                                                open && "rotate-90",
                                            )}
                                        >
                                            <path d="m9 18 6-6-6-6" />
                                        </svg>
                                        <span>{several ? C.disclosureDocument(total) : C.disclosureField(total)}</span>
                                        <span
                                            className={cn(
                                                "ml-auto rounded-full px-2.5 py-0.5 text-xs font-semibold leading-4",
                                                pillClass,
                                            )}
                                        >
                                            {keepAll
                                                ? C.pillKeepingAll
                                                : discardAll
                                                  ? C.pillDiscardingAll
                                                  : C.pillCustom(kept, total)}
                                        </span>
                                    </button>

                                    {open && (
                                        <div
                                            // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- a scroll region must be keyboard reachable
                                            tabIndex={0}
                                            aria-label={C.listLabel}
                                            className={cn(
                                                "border-t border-gray-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-max-900",
                                                flow ? "flex-none" : "min-h-0 flex-initial overflow-y-auto",
                                            )}
                                        >
                                            {documents.map((d, di) => {
                                                const keys = d.rows.map((r) => keyOf(di, r.id));
                                                const dKept = keys.filter((k) => !discarded[k]).length;
                                                return (
                                                    <div key={di} className="flex flex-col">
                                                        <div className="flex flex-col gap-2 border-b border-gray-200 bg-slate-50 px-3.5 py-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                                            <span className="flex min-w-0 items-center gap-2">
                                                                <span className="min-w-0 break-words text-sm font-bold leading-5">
                                                                    {d.header}
                                                                </span>
                                                                <span className="flex-none whitespace-nowrap text-xs leading-4 text-gray-500">
                                                                    {C.editCount(d.rows.length)}
                                                                </span>
                                                            </span>
                                                            {several && (
                                                                <SegmentedToggle
                                                                    label={d.header}
                                                                    keepLabel={C.keepAllToggle}
                                                                    discardLabel={C.discardAllToggle}
                                                                    keep={dKept === keys.length}
                                                                    discard={dKept === 0}
                                                                    onKeep={() => setMany(keys, false)}
                                                                    onDiscard={() => setMany(keys, true)}
                                                                />
                                                            )}
                                                        </div>
                                                        {d.rows.map((r) => {
                                                            const k = keyOf(di, r.id);
                                                            const section = rowSectionLabel(r);
                                                            return (
                                                                <div
                                                                    key={r.id}
                                                                    className={cn(
                                                                        "flex flex-col gap-2 border-b border-gray-100 px-3.5 pb-3.5 pt-3",
                                                                        r.nested &&
                                                                            "ml-3.5 border-l-2 border-l-gray-200 pl-4",
                                                                    )}
                                                                >
                                                                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                                                                        <h4 className="m-0 flex min-w-0 flex-wrap items-center gap-1.5 text-sm font-semibold leading-5 text-gray-900">
                                                                            {section && (
                                                                                <>
                                                                                    <span className="font-medium text-gray-500">
                                                                                        {section}
                                                                                    </span>
                                                                                    <svg
                                                                                        width="14"
                                                                                        height="14"
                                                                                        viewBox="0 0 24 24"
                                                                                        fill="none"
                                                                                        stroke="currentColor"
                                                                                        strokeWidth="2"
                                                                                        strokeLinecap="round"
                                                                                        strokeLinejoin="round"
                                                                                        aria-hidden="true"
                                                                                        className="flex-none text-gray-400"
                                                                                    >
                                                                                        <path d="m9 18 6-6-6-6" />
                                                                                    </svg>
                                                                                </>
                                                                            )}
                                                                            <span>{r.name}</span>
                                                                            {!r.tag &&
                                                                                differsOnlyInWhitespace(
                                                                                    r.baseline,
                                                                                    r.current,
                                                                                ) && (
                                                                                    <span
                                                                                        className={cn(
                                                                                            "rounded-full px-2 py-0.5 text-xs font-semibold leading-4",
                                                                                            "bg-gray-100 text-gray-700",
                                                                                        )}
                                                                                    >
                                                                                        {C.spacingOnlyTag}
                                                                                    </span>
                                                                                )}
                                                                            {r.tag && (
                                                                                <span
                                                                                    className={cn(
                                                                                        "rounded-full px-2 py-0.5 text-xs font-semibold leading-4",
                                                                                        TAG_CLASS[r.tag],
                                                                                    )}
                                                                                >
                                                                                    {TAG_LABELS[r.tag]}
                                                                                </span>
                                                                            )}
                                                                        </h4>
                                                                        <SegmentedToggle
                                                                            label={r.name}
                                                                            keepLabel={C.keepToggle}
                                                                            discardLabel={C.discardToggle}
                                                                            keep={!discarded[k]}
                                                                            discard={!!discarded[k]}
                                                                            onKeep={() => setMany([k], false)}
                                                                            onDiscard={() => setMany([k], true)}
                                                                        />
                                                                    </div>
                                                                    <EditRowDiff row={r} />
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                );
                                            })}
                                            <div
                                                aria-hidden="true"
                                                hidden={flow}
                                                className="pointer-events-none sticky bottom-0 -mt-6 h-6 bg-gradient-to-b from-white/0 to-white"
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="flex flex-none items-center justify-end gap-3 border-t border-gray-200 px-6 pt-4">
                                {showCancel && (
                                    <button type="button" className={EDIT_SECONDARY_BUTTON} onClick={onCancel}>
                                        {C.cancel}
                                    </button>
                                )}
                                <button type="button" className={EDIT_PRIMARY_BUTTON} onClick={confirm}>
                                    {confirmLabel ?? copy.confirm}
                                </button>
                            </div>
                        </DialogPanel>
                    </TransitionChild>
                </div>
            </Dialog>
        </Transition>
    );
};

export default KeepEditsModal;
